import { USER_ACTIVATION_METHOD } from '../../domain/domainCodes.js'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Building2, Copy, Globe, Mail } from 'lucide-react'
import Button from '../../components/Button'
import Card from '../../components/Card'
import Checkbox from '../../components/Checkbox'
import { FormBody, FormCols, FormSection } from '../../components/Form'
import FormRow from '../../components/FormRow'
import IdentityBand from '../../components/IdentityBand'
import IconButton from '../../components/IconButton'
import PageHeader from '../../components/PageHeader'
import useGoBack from '../../components/useGoBack'
import Select from '../../components/Select'
import PageLoader from '../../components/PageLoader'
import { DeniedState, ErrorState } from '../../components/States'
import { useToast } from '../../components/toastContext'
import TextField from '../../components/TextField'
import { getCompany, updateCompany } from './organisationsService'
import { companyProfileStrings as S, licenseInformationStrings as L } from './strings'
import './organisations.css'
import { serverDetail, serverText } from '../../lib/i18n'

const REGISTER_URL = '/Signup'

/** معلومات المنظمة — Views/CompanyProfile/Index.cshtml, reached from a قائمة المنظمات row. */
export default function CompanyProfilePage() {
  const toast = useToast()
  const { id } = useParams()
  const goBack = useGoBack('/companies')
  const [data, setData] = useState(null)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  /* THE SCREEN HAD NO FAILURE PATH. `/companies` is guarded by `SystemManagers` and
     `/api/company-profile` by `SettingsManagers`, so the one role that can open the
     organisations list is refused the profile its own rows link to. With no `catch` the 403
     became an unhandled rejection, `form` stayed null, and the page held its loader for
     ever — the screen never said anything at all. */
  const [loadError, setLoadError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoadError(null)

    getCompany(id).then((response) => {
      if (cancelled) return
      setData(response)
      const company = response.company
      /* A CoEIA admin has no organisation of their own, so there is nothing to edit
         and the server names where they belong instead. */
      if (response.redirectTo) {
        window.location.assign(response.redirectTo)
        return
      }

      setForm({
        englishName: company?.englishName ?? '',
        arabicName: company?.arabicName ?? '',
        email: company?.email ?? '',
        supportEmail: company?.supportEmail ?? '',
        supportPhone: company?.supportPhone ?? '',
        authenticationId: company?.authenticationId ?? '',
        activationBy: company?.activateByAdmin ? String(USER_ACTIVATION_METHOD.ADMIN) : String(USER_ACTIVATION_METHOD.EMAIL),
        userEmailOnDomain: !!company?.userEmailOnDomain,
        companyDomains: company?.companyDomains ?? '',
      })
    })
    .catch((error) => {
      if (cancelled) return
      // The ERROR, not just its sentence: `denied` is what tells a refusal from a failure.
      setLoadError(error)
    })

    return () => {
      cancelled = true
    }
  }, [id])

  const set = (key) => (value) => setForm((previous) => ({ ...previous, [key]: value }))

  const submit = async (event) => {
    event.preventDefault()
    setSaving(true)
    const response = await updateCompany(id, form)
    setSaving(false)

    /* The failure path used to show the SUCCESS string, so a rejected save read as a
       saved one. The command is validated server-side — a missing name or a malformed
       address comes back as that rule's own message. */
    if (response.ok) toast.success(serverText(response.message) || S.saved)
    else toast.error(serverDetail(response.message) || S.saveFailed)
  }

  if (loadError) {
    return (
      <>
        <PageHeader title={S.title} onBack={goBack} />
        <Card>
          {/* This screen's whole finding was a 403: `/companies` is `SystemManagers` and
              `/api/company-profile` is `SettingsManagers`. Saying "something went wrong"
              for that sent people to retry a screen their role cannot open. */}
          {loadError.denied ? (
            <DeniedState description={loadError.message || undefined} />
          ) : (
            <ErrorState title={S.errorHeading} description={loadError.message || S.loadFailed} />
          )}
        </Card>
      </>
    )
  }

  // Every field is controlled, so the form waits for its values.
  if (!form) {
    return (
      <>
        <PageHeader title={S.title} onBack={goBack} />
        <PageLoader />
      </>
    )
  }

  return (
    <>
      <PageHeader title={S.title} onBack={goBack} />

      {/* Which organisation this is — the same band the person pages wear, so a
          profile screen reads the same whoever it is about. */}
      <IdentityBand
        name={form.arabicName || form.englishName}
        meta={[
          ...(form.englishName ? [{ icon: Building2, text: form.englishName, ltr: true }] : []),
          ...(form.companyDomains ? [{ icon: Globe, text: form.companyDomains, ltr: true }] : []),
          ...(form.email ? [{ icon: Mail, text: form.email, ltr: true }] : []),
        ]}
      />

      {/*
       * Three ragged columns became three named sections.
       *
       * The columns held four, four and three rows of unrelated things, so they
       * ended at three different heights and nothing said why a field was in
       * the column it was in. Grouped by what they DO — who the organisation
       * is, how to reach it, how people get in — the page explains itself and
       * lines up. Same FormSection/FormCols vocabulary as every drawer.
       */}
      <form className="org-panel" onSubmit={submit}>
        <FormBody>
          <FormSection>
            <FormCols>
              <FormRow label={S.arabicName}>
                {(id_) => (
                  <TextField
                    id={id_}
                    value={form.arabicName}
                    onChange={set('arabicName')}
                    required
                  />
                )}
              </FormRow>
              <FormRow label={S.englishName}>
                {(id_) => (
                  <TextField
                    id={id_}
                    value={form.englishName}
                    onChange={set('englishName')}
                    dir="ltr"
                    required
                  />
                )}
              </FormRow>
            </FormCols>
          </FormSection>

          <FormSection>
            <FormCols>
              <FormRow label={S.email}>
                {(id_) => (
                  <TextField
                    id={id_}
                    value={form.email}
                    onChange={set('email')}
                    dir="ltr"
                    required
                  />
                )}
              </FormRow>
              <FormRow label={S.supportEmail}>
                {(id_) => (
                  <TextField
                    id={id_}
                    value={form.supportEmail}
                    onChange={set('supportEmail')}
                    dir="ltr"
                    required
                  />
                )}
              </FormRow>
              <FormRow label={S.supportPhone}>
                {(id_) => (
                  <TextField
                    id={id_}
                    value={form.supportPhone}
                    onChange={set('supportPhone')}
                    dir="ltr"
                    required
                  />
                )}
              </FormRow>
            </FormCols>
          </FormSection>

          <FormSection>
            <FormCols>
              <FormRow label={S.authMethod}>
                {(id_) => (
                  <Select
                    id={id_}
                    value={form.authenticationId}
                    onChange={set('authenticationId')}
                    options={(data?.authMethods ?? []).map((method) => ({
                      value: method.id,
                      label: method.name,
                    }))}
                  />
                )}
              </FormRow>
              <FormRow label={S.activation}>
                {(id_) => (
                  <Select
                    id={id_}
                    value={form.activationBy}
                    onChange={set('activationBy')}
                    options={[
                      { value: String(USER_ACTIVATION_METHOD.EMAIL), label: S.byEmail },
                      { value: String(USER_ACTIVATION_METHOD.ADMIN), label: S.byAdmin },
                    ]}
                  />
                )}
              </FormRow>
              <FormRow label={S.domains}>
                {(id_) => (
                  <TextField
                    id={id_}
                    value={form.companyDomains}
                    onChange={set('companyDomains')}
                    dir="ltr"
                  />
                )}
              </FormRow>
              <FormRow label={S.licenseType}>
                {/* The licence payload is encrypted in dbo.License, so the type is not
                    readable from the dump — رخصة الاستخدام shows the same placeholder. */}
                <p className="org-readonly">
                  <bdi>{L.unavailable}</bdi>
                </p>
              </FormRow>
              <FormRow className="form-span" label={S.emailOnDomain}>
                {(id_) => (
                  <Checkbox
                    id={id_}
                    checked={form.userEmailOnDomain}
                    onChange={set('userEmailOnDomain')}
                  />
                )}
              </FormRow>
              <FormRow className="form-span" label={S.registerLinkLabel}>
                <div className="org-inline">
                  <IconButton
                    icon={Copy}
                    label={S.registerLinkLabel}
                    onClick={() => navigator.clipboard?.writeText(REGISTER_URL)}
                  />
                  <a className="org-link" href={REGISTER_URL}>
                    <bdi>{S.registerLink}</bdi>
                  </a>
                </div>
              </FormRow>
            </FormCols>
          </FormSection>
        </FormBody>

        <div className="org-panel__footer">
          <Button type="submit" disabled={saving}>
            {saving ? '…' : S.update}
          </Button>
        </div>
      </form>

    </>
  )
}
